import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../theme';

/**
 * Timer (countdown) badge component.
 *
 * Props:
 * - totalSeconds: number (required) total countdown seconds
 * - running: boolean (default true) start/pause control
 * - onComplete: function called when reaches 0
 * - onTick: function called every second with remaining seconds
 * - showIcon: boolean (default true)
 * - variant: 'pill' | 'text' (default 'pill')
 * - sx: MUI sx overrides
 */
const Timer = ({
  totalSeconds = 0,
  running = true,
  onComplete,
  onTick,
  showIcon = true,
  variant = 'pill',
  sx,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [remaining, setRemaining] = React.useState(() => Math.max(0, Math.floor(totalSeconds)));

  React.useEffect(() => {
    setRemaining(Math.max(0, Math.floor(totalSeconds)));
  }, [totalSeconds]);

  React.useEffect(() => {
    if (!running || remaining <= 0) return;
    const id = setInterval(() => {
      setRemaining((prev) => {
        const next = Math.max(0, prev - 1);
        if (typeof onTick === 'function') onTick(next);
        if (next === 0 && typeof onComplete === 'function') onComplete();
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [running, remaining, onComplete, onTick]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const timeText = `${mm}:${ss}`;

  if (variant === 'text') {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, ...sx }}>
        {showIcon && <TimerOutlinedIcon fontSize="small" sx={{ color: colors.grey[600] }} />}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{timeText}</Typography>
      </Box>
    );
  }

  // pill variant
  return (
    <Chip
      size="small"
      icon={showIcon ? <TimerOutlinedIcon /> : undefined}
      label={timeText}
      sx={{
        bgcolor: colors.grey[100],
        color: colors.grey[800],
        borderRadius: 999,
        '& .MuiChip-icon': { color: colors.grey[600] },
        ...sx,
      }}
    />
  );
};

export default Timer;
